import { getUserId, getVisitorUid } from '@futures-platform/connectors/session'
import { SET_ASSESSMENT_DATA, SET_ASSESSMENT_SUMMARY } from './actionTypes'
import { ratingApi } from '../helpers/fetcher'
import { commentingApi } from '../helpers/commentingFetcher'

/**
 * Convenience thunk: reads groupId/radarId from Redux state and re-fetches
 * per-user assessment data. Call this after any user interaction (vote/rate/comment)
 * to refresh indicator states without requiring explicit parameters.
 */
export const refreshAssessmentData = () => (dispatch, getState) => {
  const radarId = getState().radarSettings?.id
  const groupId = getState().radarSettings?.group?.id
  if (groupId && radarId) dispatch(fetchAssessmentData(groupId, radarId))
}

/**
 * Fetch per-phenomenon assessment data for all three collaboration tools.
 *
 * Makes 5 parallel API calls to build a complete, self-contained map:
 *   { [phenomenonId]: { hasVotes, hasRatings, hasComments, hasOwnVote, hasOwnRating, hasOwnComment } }
 *
 * Does NOT rely on vote_sum/rating_x/y being pre-populated on phenomenon objects,
 * so it works correctly after refetchRadarData (WebSocket reloads) too.
 */
export const fetchAssessmentData = (groupId, radarId) => async (dispatch, getState) => {
  try {
    const currentUserId = String(getUserId() || '')
    const visitorUid    = String(getVisitorUid() || '')

    const [allVotesRes, allRatingsRes, votingCurrentUserRes, ratingCurrentUserRes, commentsRes, ownCommentsRes] = await Promise.allSettled([
      ratingApi.getAllVotes(groupId, radarId),                         // hasVotes (all users) — voting-api
      ratingApi.getAllRatings(groupId, radarId),                       // hasRatings (all users) — voting-api/rating/...
      ratingApi.getVotingsCurrentUserOnly1Api(groupId, radarId),      // hasOwnVote — voting-api
      ratingApi.getRatingsCurrentUserOnly1Api(groupId, radarId),      // hasOwnRating — voting-api
      commentingApi.getAllComments(groupId, radarId),                  // hasComments — commenting-api
      commentingApi.getCommentsByCurrentUser(groupId, radarId),       // hasOwnComment — commenting-api
    ])

    // ── 1. Build set of phenomenon IDs that have ANY votes ────────────────────
    const votedPhenIds = new Set()
    if (allVotesRes.status === 'fulfilled') {
      const allVotesData = allVotesRes.value?.data
      const votesObj = allVotesData && typeof allVotesData === 'object' ? allVotesData : {}
      // Shape: { [entityKey]: { sum, up, down } }  or array of vote objects
      if (Array.isArray(votesObj)) {
        votesObj.forEach(item => {
          const uri = item?.entityUri || item?.entity_uri || ''
          const pid = String(uri).split('/')[5]
          if (pid && (Math.abs(Number(item?.sum || 0)) > 0 || item?.up || item?.down)) votedPhenIds.add(pid)
        })
      } else {
        Object.entries(votesObj).forEach(([, phenVotes]) => {
          if (phenVotes && typeof phenVotes === 'object') {
            Object.entries(phenVotes).forEach(([uri, data]) => {
              const pid = String(uri).split('/')[5]
              // API may return `sum` (aggregated) or `plus_votes`/`minus_votes` separately
              const total = Math.abs(Number(data?.sum || 0))
                + Number(data?.plus_votes || 0)
                + Number(data?.minus_votes || 0)
              if (pid && total > 0) votedPhenIds.add(pid)
            })
          }
        })
      }
    }
    // Fallback: also check vote_sum on phenomenon objects in Redux (set during initial fetchRadar)
    const phenomenaRaw = getState().phenomena || []
    const phenomena = Array.isArray(phenomenaRaw)
      ? Object.fromEntries(phenomenaRaw.map(p => [String(p.id), p]))
      : phenomenaRaw
    Object.entries(phenomena).forEach(([pid, phen]) => {
      if (Math.abs(Number(phen?.vote_sum || 0)) > 0) votedPhenIds.add(pid)
    })

    // ── 2. Build set of phenomenon IDs that have ANY ratings ─────────────────
    const ratedPhenIds = new Set()
    if (allRatingsRes.status === 'fulfilled') {
      const ratingsData = allRatingsRes.value?.data
      if (ratingsData && typeof ratingsData === 'object') {
        // Shape: { /{gid}/radar/{radarId}/phenomenon/{pid}/x: {...}, .../y: {...} }
        Object.keys(ratingsData).forEach(key => {
          const pid = String(key).split('/')[5]
          if (pid) ratedPhenIds.add(pid)
        })
      }
    }
    // Fallback: check rating_x / rating_y on phenomenon objects
    Object.entries(phenomena).forEach(([pid, phen]) => {
      if (phen?.rating_x || phen?.rating_y) ratedPhenIds.add(pid)
    })

    // ── 3. Build set of phenomenon IDs the current user has voted on ──────────
    const ownVotePhenIds = new Set()
    if (votingCurrentUserRes.status === 'fulfilled') {
      const votingData = votingCurrentUserRes.value?.data
      const votingArray = Array.isArray(votingData) ? votingData : []
      votingArray.forEach(item => {
        // API may return camelCase `entityUri` or snake_case `entity_uri`
        // entityUri format: "/<gid>/radar/<radarId>/phenomenon/<pid>"
        const uri = item?.entityUri || item?.entity_uri || ''
        if (uri) {
          const parts = uri.split('/')
          const phenIdx = parts.indexOf('phenomenon')
          const pid = phenIdx !== -1 ? parts[phenIdx + 1] : undefined
          if (pid) ownVotePhenIds.add(pid)
        }
      })
    }

    // ── 4. Build set of phenomenon IDs the current user has rated ─────────────
    const ownRatingPhenIds = new Set()
    if (ratingCurrentUserRes.status === 'fulfilled') {
      const ratingData = ratingCurrentUserRes.value?.data
      if (ratingData && typeof ratingData === 'object') {
        Object.keys(ratingData).forEach(key => {
          // key format: /{gid}/radar/{radarId}/phenomenon/{pid}/x  or  /y
          const parts = key.split('/')
          const pid = parts[5]
          if (pid) ownRatingPhenIds.add(pid)
        })
      }
    }

    // ── 5. Parse comments: build per-phenomenon hasComments + hasOwnComment ───
    const commentsHasAny    = {}   // { [pid]: boolean }
    const commentsHasOwn    = {}   // { [pid]: boolean }

    if (commentsRes.status === 'fulfilled') {
      const commentsArray = commentsRes.value?.data
      const allComments = Array.isArray(commentsArray)
        ? commentsArray
        : Object.values(commentsArray || {})

      allComments.forEach(comment => {
        // entity_uri may be:
        //   "commenting/{gid}/radar/{radarId}/phenomenon/{pid}/{section}"  (no leading slash)
        //   "/commenting/{gid}/radar/{radarId}/phenomenon/{pid}/{section}" (leading slash)
        // Find "phenomenon" segment and take the next part as pid.
        const uri   = comment?.entity_uri || ''
        const parts = uri.split('/')
        const phenIdx = parts.indexOf('phenomenon')
        const pid = phenIdx !== -1 ? parts[phenIdx + 1] : undefined
        if (pid) commentsHasAny[pid] = true
      })
    }

    // Build own-comment set from the dedicated /user endpoint (mirrors voting/rating pattern)
    if (ownCommentsRes.status === 'fulfilled') {
      const ownCommentsArray = ownCommentsRes.value?.data
      const ownComments = Array.isArray(ownCommentsArray)
        ? ownCommentsArray
        : Object.values(ownCommentsArray || {})

      ownComments.forEach(comment => {
        const uri   = comment?.entity_uri || ''
        const parts = uri.split('/')
        const phenIdx = parts.indexOf('phenomenon')
        const pid = phenIdx !== -1 ? parts[phenIdx + 1] : undefined
        if (pid) {
          commentsHasAny[pid] = true  // own comment implies any activity
          commentsHasOwn[pid] = true
        }
      })
    } else {
      // Fallback: if /user endpoint fails, match uid against currentUserId
      if (commentsRes.status === 'fulfilled') {
        const commentsArray = commentsRes.value?.data
        const allComments = Array.isArray(commentsArray)
          ? commentsArray
          : Object.values(commentsArray || {})
        allComments.forEach(comment => {
          const uri   = comment?.entity_uri || ''
          const parts = uri.split('/')
          const phenIdx = parts.indexOf('phenomenon')
          const pid = phenIdx !== -1 ? parts[phenIdx + 1] : undefined
          if (!pid) return
          const commentUserId = String(comment?.uid || '')
          // Normal users: uid is numeric, matches currentUserId
          // Public-share users: uid is "{groupId}/{visitorUuid}" — match by visitorUid
          const isOwnComment = (currentUserId && commentUserId === currentUserId)
            || (visitorUid && commentUserId.includes(visitorUid))
          if (isOwnComment) commentsHasOwn[pid] = true
        })
      }
    }

    // ── 6. Merge: build per-phenomenon indicator state map ────────────────────
    const allPhenomenaIds = new Set([
      ...Object.keys(phenomena),
      ...votedPhenIds,
      ...ratedPhenIds,
      ...ownVotePhenIds,
      ...ownRatingPhenIds,
      ...Object.keys(commentsHasAny),
    ])

    const perPhenomenon = {}
    allPhenomenaIds.forEach(pid => {
      const pidStr = String(pid)

      // Use the freshly-fetched sets — do NOT rely on vote_sum/rating_x on phenomena
      const hasVotes    = votedPhenIds.has(pidStr)
      const hasRatings  = ratedPhenIds.has(pidStr)
      const hasComments = !!commentsHasAny[pidStr]

      perPhenomenon[pidStr] = {
        hasVotes,
        hasRatings,
        hasComments,
        hasOwnVote:    ownVotePhenIds.has(pidStr),
        hasOwnRating:  ownRatingPhenIds.has(pidStr),
        hasOwnComment: !!commentsHasOwn[pidStr],
      }
    })

    dispatch({ type: SET_ASSESSMENT_DATA, payload: perPhenomenon })
  } catch (err) {
    console.error('[assessmentData] fetchAssessmentData error:', err)
  }
}

/**
 * Fetch aggregate assessment summary for the editor control panel (spec §2).
 *
 * Uses three dedicated count APIs — each returns per-user-type breakdowns for
 * users, phenomena and actions in a single call. No need to parse getAllVotes /
 * getAllRatings / getAllComments.
 *
 * usersTotal: coordinatorAndEditorsUsed + usersAllowed + visitorsUsed from the
 * group object already in Redux state (loaded on app init).
 *
 * Display format (spec §2.3):
 *   "{usersDone}/{usersTotal} registered users: voted on {phenDone}/{phenTotal} phenomena ({actionsTotal} votes)"
 *   "Unknown users: voted on {phenDone}/{phenTotal} phenomena ({actionsTotal} votes)"
 */
export const fetchAssessmentSummary = (groupId, radarId) => async (dispatch, getState) => {
  try {
    const state = getState()

    // ── phenTotal: total phenomena in this radar ──────────────────────────────
    const phenomenaRaw = state.phenomena || []
    const phenTotal = Array.isArray(phenomenaRaw)
      ? phenomenaRaw.length
      : Object.keys(phenomenaRaw).length

    // ── usersTotal: sum of all licensed seats from group object already in Redux
    // Group API (called on app init): GET /api/v1.0/group?filter[id]=:groupId
    // Returns: coordinatorAndEditorsUsed + usersAllowed + visitorsUsed
    const groups = state.radarSettings?.groups || []
    const group  = groups.find(g => String(g.id) === String(groupId) || String(g.value) === String(groupId))
    const usersTotal =
      Number(group?.usersUsed ?? 0) +
      Number(group?.visitorsUsed ?? 0) +
      Number(group?.coordinatorAndEditorsUsed ?? 0)

    // ── 3 parallel count API calls — each has all the data we need ────────────
    const [voteCountsRes, rateCountsRes, commentCountsRes] = await Promise.allSettled([
      ratingApi.getVoteCounts(groupId, radarId),
      ratingApi.getRateCounts(groupId, radarId),
      commentingApi.getCommentCounts(groupId, radarId),
    ])

    const voteCounts    = voteCountsRes.status    === 'fulfilled' ? (voteCountsRes.value?.data    ?? voteCountsRes.value    ?? {}) : {}
    const rateCounts    = rateCountsRes.status    === 'fulfilled' ? (rateCountsRes.value?.data    ?? rateCountsRes.value    ?? {}) : {}
    const commentCounts = commentCountsRes.status === 'fulfilled' ? (commentCountsRes.value?.data ?? commentCountsRes.value ?? {}) : {}

    // ── Build summary ─────────────────────────────────────────────────────────
    const summary = {
      voting: {
        registeredUsers: {
          usersDone:      Number(voteCounts.normal_user_count      ?? 0),
          usersTotal,
          phenomenaDone:  Number(voteCounts.normal_phenomena_count ?? 0),
          phenomenaTotal: phenTotal,
          actionsTotal:   Number(voteCounts.normal_vote_count      ?? 0),
        },
        unknownUsers: {
          usersDone:      Number(voteCounts.public_user_count      ?? 0),
          phenomenaDone:  Number(voteCounts.public_phenomena_count ?? 0),
          phenomenaTotal: phenTotal,
          actionsTotal:   Number(voteCounts.public_vote_count      ?? 0),
        },
      },
      rating: {
        registeredUsers: {
          usersDone:      Number(rateCounts.normal_user_count      ?? 0),
          usersTotal,
          phenomenaDone:  Number(rateCounts.normal_phenomena_count ?? 0),
          phenomenaTotal: phenTotal,
          actionsTotal:   Number(rateCounts.normal_rate_count      ?? 0),
        },
        unknownUsers: {
          usersDone:      Number(rateCounts.public_user_count      ?? 0),
          phenomenaDone:  Number(rateCounts.public_phenomena_count ?? 0),
          phenomenaTotal: phenTotal,
          actionsTotal:   Number(rateCounts.public_rate_count      ?? 0),
        },
      },
      commenting: {
        registeredUsers: {
          usersDone:      Number(commentCounts.normal_user_count      ?? 0),
          usersTotal,
          phenomenaDone:  Number(commentCounts.normal_phenomena_count ?? 0),
          phenomenaTotal: phenTotal,
          actionsTotal:   Number(commentCounts.normal_comment_count   ?? 0),
        },
        unknownUsers: {
          usersDone:      Number(commentCounts.public_user_count      ?? 0),
          phenomenaDone:  Number(commentCounts.public_phenomena_count ?? 0),
          phenomenaTotal: phenTotal,
          actionsTotal:   Number(commentCounts.public_comment_count   ?? 0),
        },
      },
    }

    dispatch({ type: SET_ASSESSMENT_SUMMARY, payload: summary })
  } catch (err) {
    console.error('[assessmentData] fetchAssessmentSummary error:', err)
  }
}


